import { useMemo } from "react";

import Fuse from "fuse.js";
import { useLazyLoadQuery } from "react-relay";

import { ContentPlatformContentQueryInput } from "@/__generated__/templateInitialSearchResultsRelayQuery.graphql";
import { useSearchSuggestionsQuery } from "@/__generated__/useSearchSuggestionsQuery.graphql";

import { CategoryTemplateOrder } from "../../config/category-template-order";
import { useQueryLocale } from "../hooks/use-query-locale";
import {
    FUSE_OPTIONS,
    parseCategorySearchItems,
    parseTemplateSearchItems,
    UseSearchSuggestionsQuery
} from "../hooks/use-search-suggestions";

import { CategorySearchSuggestion } from "./template-category-search-results-relay";
import { TemplateInitialSearchResults } from "./template-initial-search-results-relay";

function parseSearchQueryFromSuggestions(
    templateMatches: TemplateSearchSuggestion[],
    categoryMatches: CategorySearchSuggestion[],
    withQuery?: Partial<Exclude<ContentPlatformContentQueryInput, "where">>
): ContentPlatformContentQueryInput {
    const clauses = [];
    const templateIds = templateMatches
        .map((result) => result.id)
        .filter((id) => typeof id === "string");

    if (templateIds.length > 0) {
        clauses.push({
            fieldNamed: "urlSlug",
            havingValues: templateIds
        });
    }

    const categoryIds = categoryMatches
        .map((result) => result.id)
        .filter((id) => typeof id === "string");

    if (categoryIds.length > 0) {
        clauses.push({
            fieldNamed: "category.urlSlug",
            havingValues: categoryIds
        });
    }

    return {
        ...withQuery,
        where: clauses.length > 1 ? { or: clauses } : clauses[0]
    };
}

export const isSearchType = (
    searchTerm:
        | CategorySearchSuggestion
        | TemplateSearchSuggestion
        | string
        | null
): searchTerm is string => {
    return typeof searchTerm === "string";
};

export const isTemplateType = (
    searchTerm:
        | CategorySearchSuggestion
        | TemplateSearchSuggestion
        | string
        | null
): searchTerm is TemplateSearchSuggestion => {
    return typeof searchTerm !== "string" && searchTerm?.type === "template";
};

export const isCategoryType = (
    searchTerm:
        | CategorySearchSuggestion
        | TemplateSearchSuggestion
        | string
        | null
): searchTerm is CategorySearchSuggestion => {
    return typeof searchTerm !== "string" && searchTerm?.type === "category";
};

export type TemplateSearchSuggestion = {
    type: "template";
    id: string;
    name?: string | null;
    description?: string | null;
    contributor?: string | null;
    contributorOrganization?: string | null;
    customTag?: string[] | null;
    templateType?: string | null;
};

export type TemplateFuzzySearchResultsProps = {
    searchTerm: string | TemplateSearchSuggestion;
    /**
     * A unique key to identify the search term.
     * This is required to force a new instance
     * of `TemplatePaginatedSearchResults`
     * when the search term changes.
     */
    searchKey: string;
};

export const TemplateFuzzySearchResults = ({
    searchTerm,
    searchKey
}: TemplateFuzzySearchResultsProps) => {
    const locale = useQueryLocale();
    const suggestionData = useLazyLoadQuery<useSearchSuggestionsQuery>(
        UseSearchSuggestionsQuery,
        { locale }
    );

    const fuse = useMemo(() => {
        return new Fuse(
            [
                ...parseTemplateSearchItems(suggestionData.templates),
                ...parseCategorySearchItems(suggestionData.categories)
            ],
            FUSE_OPTIONS
        );
    }, [suggestionData]);

    const [templateMatches, categoryMatches] = useMemo(() => {
        const matches = fuse
            .search(
                typeof searchTerm === "string"
                    ? searchTerm
                    : searchTerm.name ?? searchTerm.id
            )
            .sort((a, b) => (b.score ?? Infinity) - (a.score ?? Infinity)) // Sort by score in descending order
            .map((result) => result.item);

        if (isTemplateType(searchTerm)) {
            // Take the first template match and discard all other matches.
            const match = matches.find(
                (item) => item.type === "template" && item.id === searchTerm.id
            );
            if (match) {
                return [[match], []] as [
                    TemplateSearchSuggestion[],
                    CategorySearchSuggestion[]
                ];
            }
        }

        return matches
            .slice(0, 35) // Get the top 35 results
            .reduce<[TemplateSearchSuggestion[], CategorySearchSuggestion[]]>(
                (acc, result) => {
                    if (result.type === "template") {
                        acc[0].push(result);
                    } else if (result.type === "category") {
                        acc[1].push(result);
                    }
                    return acc;
                },
                [[], []]
            );
    }, [fuse, searchTerm]);

    const sortLookup = useMemo(() => {
        const lookup: Record<string, number> = {};
        let rank = 1;

        // Rank template matches before category matches.
        for (const template of templateMatches) {
            if (template.type === "template") {
                lookup[template.id] = rank;
                rank++;
            }
        }
        // Use hardcoded category order for ranking category matches.
        for (const category of categoryMatches) {
            if (category.type === "category") {
                const categoryLookup =
                    CategoryTemplateOrder[
                        category.id as keyof typeof CategoryTemplateOrder
                    ];
                if (categoryLookup) {
                    for (const slug in categoryLookup) {
                        // Exclude templates that are already ranked.
                        if (!(slug in lookup)) {
                            lookup[slug] = rank;
                            rank++;
                        }
                    }
                }
            }
        }

        return lookup;
    }, [categoryMatches, templateMatches]);

    const searchQuery = useMemo(() => {
        return parseSearchQueryFromSuggestions(
            templateMatches,
            categoryMatches,
            { first: Object.keys(sortLookup).length, withLocales: [locale] }
        );
    }, [categoryMatches, locale, sortLookup, templateMatches]);

    return (
        <TemplateInitialSearchResults
            searchKey={searchKey}
            searchQuery={searchQuery}
            sortLookup={sortLookup}
        />
    );
};
